/**
 * MANAGEMENT (14) — metrics wire shapes. The EXACT client view of the two cross-lab aggregation endpoints
 * the super-admin Overview cockpit consumes, verified against
 * server/src/modules/management/metrics.service.js:
 *   GET /management/metrics/overview  → {@link OverviewMetrics}  (point-in-time KPIs + distributions + deltas)
 *   GET /management/metrics/trends    → {@link TrendMetrics}     (time-series for the chart region)
 *
 * SCHEMA-FREE / DERIVE-FROM-TIMESTAMPS. The server reconstructs every figure from created_at/updated_at/
 * resolu_at + statut — there is no snapshot history. Counts are point-in-time; period-over-period deltas use
 * windows. All ids/counts arrive as plain Numbers (the server closes the BigInt-serialization trap). NO PHI
 * rides either seam (the management plane is operational, L-4). These types are additive to ./types.ts.
 */
import type { SubscriptionStatut, TicketCategorie, TicketStatut } from "./types";

/** The trend window the period picker offers — drives bucketing server-side (daily ≤90d, monthly = 12mo). */
export type MetricsPeriod = "30d" | "90d" | "12mo";

/** The list of periods, in display order — the single source the picker + the i18n labels read. */
export const METRICS_PERIODS: ReadonlyArray<MetricsPeriod> = ["30d", "90d", "12mo"];

// ── /management/metrics/overview ───────────────────────────────────────────────────

/** Cross-lab laboratory counts + the month-over-month new-lab delta. */
export interface OverviewLabs {
  /** Active labs (deleted_at:null). */
  total: number;
  /** Soft-deleted / suspended labs. */
  suspended: number;
  /** Labs created this calendar month (the delta numerator). */
  newThisMonth: number;
  /** Labs created the previous calendar month (the delta baseline). */
  newPrevMonth: number;
  /** Active labs grouped by deploy_class (local / cloud / hybrid / …) — a tolerant, open map. */
  byDeployClass: Record<string, number>;
}

/** Entitlement-projection distribution + a count-by-plan revenue proxy + the churn delta. */
export interface OverviewSubscriptions {
  /** Sum of the ACTIVE statuts (ESSAI + ACTIVE + PAST_DUE). */
  active: number;
  /** Complete, stable map over every known statut (server seeds missing keys to 0). */
  statusDistribution: Record<SubscriptionStatut, number>;
  /** Count-by-plan (no price table — a revenue proxy), descending. */
  planMix: Array<{ plan: string | null; count: number }>;
  /** ANNULEE within the trailing 30d. */
  churn30d: number;
  /** ANNULEE within the prior 30d (the delta baseline). */
  churnPrev30d: number;
}

/** Helpdesk distribution + SLA% + aged-open + the labo→admin unread badge. */
export interface OverviewTickets {
  /** Open work (ouvert + en_cours). */
  open: number;
  /** Complete map over every ticket statut. */
  byStatut: Record<TicketStatut, number>;
  /** Complete map over every ticket category. */
  byCategorie: Record<TicketCategorie, number>;
  /** Resolved-within-target / resolved over the trailing window. null when nothing has been resolved. */
  slaPct: number | null;
  /** The SLA target in hours (config-driven server-side). */
  slaTargetHours: number;
  /** Open tickets older than `agedDays`. */
  agedOpen: number;
  /** The aged-ticket threshold in days (config-driven). */
  agedDays: number;
  /** Approximate open-equivalent count for the prior period (delta baseline; documented approximate). */
  openPrevPeriod: number;
  /** Unread labo→admin replies (PHI-free count). */
  unread: number;
}

/** The provisioning funnel. `everLoggedIn` is OPTIONAL — present only when the server column exists. */
export interface OverviewFunnel {
  /** Every lab ever provisioned (incl. suspended). */
  provisioned: number;
  /** Labs with at least one AbonnementProjection row. */
  synced: number;
  /** Labs whose projection statut is in the active set. */
  activeSub: number;
  /** Optional 4th stage: labs whose owner has logged in (column-presence-guarded server-side). */
  everLoggedIn?: number;
}

/** The full overview payload (the cockpit's point-in-time KPI feed). */
export interface OverviewMetrics {
  labs: OverviewLabs;
  subscriptions: OverviewSubscriptions;
  tickets: OverviewTickets;
  onboardingFunnel: OverviewFunnel;
  generatedAt: string;
  limits: { note: string };
}

// ── /management/metrics/trends ──────────────────────────────────────────────────────

/** One bucketed datum on the shared x-axis. */
export interface TrendPoint {
  /** ISO date (YYYY-MM-DD) — the bucket label. */
  date: string;
  value: number;
}

/** The three aligned series the chart region renders. */
export interface TrendSeries {
  /** Running total labs through each bucket (monotonic). */
  cumulativeLabs: TrendPoint[];
  /** Tickets opened per bucket. */
  ticketsOpened: TrendPoint[];
  /** Tickets resolved per bucket. */
  ticketsResolved: TrendPoint[];
}

/** The full trends payload. */
export interface TrendMetrics {
  period: MetricsPeriod;
  bucket: "day" | "month";
  /** The ascending bucket axis (the x labels). */
  buckets: string[];
  series: TrendSeries;
  generatedAt: string;
  limits: { note: string };
}
